import React, { useRef, useState } from "react"
import { percentageBody } from "../../../../../utils/tables.utils"
import CellReportBadges, {
  TimeDifferenceBadge,
  UnexpectedTimeBadge,
} from "../../components/CellReportBadges"
import DataTable from "../../../../../components/atoms/table/DataTable.atom"
import Column from "../../../../../components/atoms/table/Column.atom"
import DifferenceReasonTable from "../../components/DifferenceReasonTable"
import { formatPercentageSuffix } from "../../../../../utils/strings.utils"
import { DateTime } from "luxon"
import ClientProjectChip from "../../../../../components/molecules/ClientProjectChip.molecule"
import { UNIFIEDDATE_VALUES } from "../../../../../consts/CompoundRangeDatePicker"
import {
  dateFormat,
  getDayName,
  isBeforeCurrentWeek,
  isDateAtWeekend,
} from "../../../../../utils/date.utils"
import IndividualReportLink from "../../../../../routers/Navigation/IndividualReportLink"
import { HOLIDAYS_TYPES_ID_TO_LABEL } from "../../../../../consts/modules/vacations.consts"
import VacationFlag from "../../../../../components/molecules/VacationFlag.molecule"
import {
  isHolidayAvailable,
  showWeeklyReportCellValue,
} from "../../../../../utils/vacation.utils"
import { BREAKPOINTS } from "../../../../../consts/responsiveBreakpoints.consts"
import useScreenDimensionsContext from "../../../../../hooks/contextConsumers/useScreenDimensionsContext.hook"
import { OverlayPanel } from "../../../../../components/atoms/OverlayPanel.atom"
import Button from "../../../../../components/atoms/Button.atom"
import { useNavigate } from "react-router-dom"
import CustomDialog from "../../../../../components/molecules/CustomDialog.molecule"
import IconWithTooltipAndCustomBody from "../../../../../components/molecules/IconWithTooltipAndCustomBody.molecule"

const TeamMemberNameTemplate = ({ rowData, schemaRules, holidays }) => {
  const startDate = Object.keys(rowData?.week)[0]
  const endDate = DateTime.fromISO(startDate).endOf("week").toISODate()
  const diffInDays = DateTime.fromISO(endDate).diff(
    DateTime.fromISO(startDate),
    "days"
  ).days
  let period = UNIFIEDDATE_VALUES.SINGLEWEEK
  if (diffInDays <= 1) {
    period = UNIFIEDDATE_VALUES.SINGLEDAY
  } else if (diffInDays <= 7) {
    period = UNIFIEDDATE_VALUES.SINGLEWEEK
  } else if (diffInDays <= 31) {
    period = UNIFIEDDATE_VALUES.SINGLEMONTH
  } else {
    period = UNIFIEDDATE_VALUES.RANGEDAY
  }
  const { screenWidth } = useScreenDimensionsContext()
  const isOnMobile = screenWidth <= BREAKPOINTS.MOBILE_BREAKPOINT

  const overlayPanelRef = useRef(null)

  const navigate = useNavigate()
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  return isOnMobile ?
      <>
        <p
          className="team-member-name"
          onClick={(e) => overlayPanelRef.current.toggle(e)}
        >
          <span className="white-space-nowrap">
            {rowData.teamMemberTagName}
          </span>
        </p>
        <OverlayPanel
          className="team-members-overlay-panel-week-hours-report"
          ref={overlayPanelRef}
        >
          <Button
            onClick={() => {
              navigate("/reporte-individual", {
                state: {
                  teamMemberId: rowData?.teamMemberId,
                  teamMemberName: rowData?.teamMemberName,
                  teamMemberLastName: rowData?.teamMemberLastName,
                  startDate,
                  endDate,
                  period,
                },
              })
            }}
            style={{
              color: "var(--text-color)",
            }}
          >
            Ir a reporte individual
          </Button>
          <Button
            onClick={() => {
              overlayPanelRef.current.hide()
              setIsDialogOpen(true)
            }}
            style={{
              color: "var(--text-color)",
            }}
          >
            Ver días de la semana
          </Button>
        </OverlayPanel>
        <CustomDialog
          visible={isDialogOpen}
          onHide={() => setIsDialogOpen(false)}
          closable
          className="week-hours-report-data-table-dialog"
          header={
            <p style={{ fontSize: "24px" }} className="font-cairo">
              {rowData.teamMemberTagName} - {rowData.teamMemberName}{" "}
              {rowData.teamMemberLastName}
            </p>
          }
          withoutDivider
          withoutFooter
          dismissableMask
        >
          <table
            className="w-full"
            style={{ borderSpacing: "0 14px", borderCollapse: "separate" }}
          >
            <tbody>
              {Object.keys(rowData.week)?.map((date) => {
                const dayName = getDayName(date)
                const isWeekend = isDateAtWeekend(date)

                const currentDay = rowData?.week?.[date]

                const getHolidayHeader = (date) => {
                  return holidays.filter(
                    (holiday) => holiday.date === date
                  )?.[0]
                }
                const holidayHeader = getHolidayHeader(date)

                return (
                  <tr key={date}>
                    <td className="p-1" style={{ width: "50%" }}>
                      <div className="flex flex-column">
                        <strong>{dayName}</strong>
                        <span className="font-normal text-sm white-space-nowrap">
                          {dateFormat(date)}
                        </span>
                      </div>
                    </td>
                    <td className="p-1 text-right" style={{ width: "50%" }}>
                      <p style={{ textAlign: "left" }}>
                        {showWeeklyReportCellValue(currentDay) ?? "0"}
                      </p>
                    </td>
                    <td
                      className="p-1 text-right"
                      style={{ width: "1px", whiteSpace: "nowrap" }}
                    >
                      <span
                        className={`flex align-items-center justify-content-end gap-1 table-cell`}
                      >
                        <CellReportBadges
                          position="bottom"
                          date={date}
                          rowData={rowData}
                        />
                        {holidayHeader && (
                          <div className="min-w-4rem flex justify-content-start">
                            <VacationFlag
                              schemaTitle={holidayHeader?.holidaySchemaName}
                              tooltipConfig={{
                                content: holidayHeader?.name,
                                position: "bottom",
                              }}
                              showTooltip
                            />
                            <div className="ml-1"></div>
                          </div>
                        )}
                        {isWeekend && currentDay?.totalSisuTime && (
                          <TimeDifferenceBadge
                            value={currentDay?.totalSisuTime}
                            minTreshold={0}
                            maxTreshold={0}
                            position="bottom"
                            tooltip={
                              "Tiempos registrados fuera de la semana laboral recomendada"
                            }
                            className="m-0"
                          />
                        )}
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </CustomDialog>
      </>
    : <IndividualReportLink
        className="team-member-name"
        state={{
          teamMemberId: rowData?.teamMemberId,
          teamMemberName: rowData?.teamMemberName,
          teamMemberLastName: rowData?.teamMemberLastName,
          startDate,
          endDate,
          period,
        }}
        title="Ir al reporte individual ↗"
      >
        <div className="flex align-items-center">
          <span className="white-space-nowrap px-2">
            {rowData.teamMemberTagName} - {rowData.teamMemberName}{" "}
            {rowData.teamMemberLastName.split(" ")[0]}
          </span>
        </div>
      </IndividualReportLink>
}

const WeekHoursDataTable = React.forwardRef(
  /**
   * @param {import("react").ComponentProps<typeof DataTable>} props
   */
  (
    {
      data: {
        WEEK_DATES,
        setModalData,
        setIsTimeDiffDetailsModalOpen,
        filteredUsers,
        allMembersLength,
        isDateBeforeAIKALaunch,
        holidays,
        schemaRules,
        teamMemberFilters,
        horizontalScrollCursor,
        setHorizontalScrollCursor,
      },
      ...otherProps
    },
    ref
  ) => {
    const { screenWidth } = useScreenDimensionsContext()
    const isOnMobile = screenWidth <= BREAKPOINTS.MOBILE_BREAKPOINT

    const increaseHCursor = () => {
      setHorizontalScrollCursor(horizontalScrollCursor + 1)
    }

    const decreaseHCursor = () => {
      setHorizontalScrollCursor(horizontalScrollCursor - 1)
    }

    const prevColItemChevron = (
      <button
        className="prev-col-item-chevron"
        type="button"
        onClick={decreaseHCursor}
      >
        <span className="pi pi-chevron-left" aria-hidden="true"></span>
        <span role="presentation" className="p-ink"></span>
      </button>
    )

    const nextColItemChevron = (
      <button
        className="next-col-item-chevron"
        type="button"
        onClick={increaseHCursor}
      >
        <span className="pi pi-chevron-right" aria-hidden="true"></span>
        <span role="presentation" className="p-ink"></span>
      </button>
    )

    function weekClientBilledTimeHeader() {
      if (screenWidth > BREAKPOINTS.MOBILE_BREAKPOINT) {
        return "Horas facturadas"
      }
      return (
        <div className="flex align-items-center">
          {horizontalScrollCursor === 1 && prevColItemChevron}
          <p className="text-overflow-ellipsis overflow-hidden mb-0 text-overflow-ellipsis white-space-nowrap">
            Facturadas
          </p>
          {horizontalScrollCursor === 0 && nextColItemChevron}
        </div>
      )
    }

    function weekTimeDifferenceHeader() {
      if (screenWidth > BREAKPOINTS.MOBILE_BREAKPOINT) {
        return "Perdonadas a clientes"
      }
      return (
        <div className="flex align-items-center">
          {horizontalScrollCursor === 2 && prevColItemChevron}
          <p className="text-overflow-ellipsis overflow-hidden mb-0 text-overflow-ellipsis white-space-nowrap">
            Perdonadas
          </p>
          {horizontalScrollCursor === 1 && nextColItemChevron}
        </div>
      )
    }

    return (
      <DataTable ref={ref} {...otherProps}>
        {/* Team member column */}
        <Column
          body={(rowData) => (
            <TeamMemberNameTemplate
              rowData={rowData}
              schemaRules={schemaRules}
              holidays={holidays}
            />
          )}
          style={
            screenWidth > BREAKPOINTS.MOBILE_BREAKPOINT ?
              {
                padding: "7px 7px 7px 7px",
                width: "400px",
                minWidth: "236px",
              }
            : {
                padding: "0px",
                paddingRight: 0,
                width: "94px",
                minWidth: "94px",
              }
          }
          field="teamMemberTagName"
          frozen={!isOnMobile}
          header={() => (
            <div className="flex align-items-center gap-1">
              Integrantes{" "}
              {screenWidth > BREAKPOINTS.MOBILE_BREAKPOINT ?
                <>
                  <span className="team-members-count-badge">
                    {filteredUsers.length === 0 ?
                      allMembersLength
                    : filteredUsers.length}
                  </span>
                  {teamMemberFilters}
                </>
              : null}
            </div>
          )}
        />
        {/* Week days columns */}
        {WEEK_DATES?.map((date) => {
          const isWeekend = isDateAtWeekend(date)
          const dayName = getDayName(date)
          return (
            <Column
              key={date}
              style={{
                minWidth: "103px",
              }}
              field={`week.${date}.totalSisuTime`}
              hidden={isOnMobile}
              header={() => {
                const getHolidayHeader = (date) => {
                  return holidays.filter(
                    (holiday) => holiday.date === date
                  )?.[0]
                }
                const holidayHeader = getHolidayHeader(date)
                return (
                  <>
                    <div className="flex w-full gap-2 align-items-center">
                      {holidayHeader && (
                        <VacationFlag
                          schemaTitle={holidayHeader?.holidaySchemaName}
                          tooltipConfig={{
                            content: holidayHeader?.name,
                            position: "right",
                          }}
                          showTooltip
                        />
                      )}
                      <div className="flex flex-column">
                        <span>{dayName}</span>
                        <span className="font-normal text-sm white-space-nowrap">
                          {dateFormat(date)}
                        </span>
                      </div>
                    </div>
                  </>
                )
              }}
              body={(rowData) => {
                const currentDay = rowData?.week?.[date]

                const teamMemberSchemaRules =
                  schemaRules[currentDay?.teamMemberHolidaySchemaId]
                const isHolidayAvailableForTeamMember = isHolidayAvailable({
                  idTypeOfHoliday: currentDay?.holidayType,
                  idHolidaySchemaType: currentDay?.holidaySchemaType,
                  teamMemberSchemaRules,
                  workAnniversaryDate: currentDay?.teamMemberWorkAnniversary,
                  currentDate: date,
                })
                const holidayClass =
                  HOLIDAYS_TYPES_ID_TO_LABEL[currentDay?.holidayType]
                return (
                  <span
                    className={`flex align-items-center gap-1 table-cell ${
                      isHolidayAvailableForTeamMember &&
                      `table-cell__${holidayClass}`
                    }`}
                  >
                    {showWeeklyReportCellValue(currentDay)}
                    <CellReportBadges date={date} rowData={rowData} />
                    {isWeekend && currentDay?.totalSisuTime && (
                      <TimeDifferenceBadge
                        value={currentDay?.totalSisuTime}
                        minTreshold={0}
                        maxTreshold={0}
                        tooltip={
                          "Tiempos registrados fuera de la semana laboral recomendada"
                        }
                        className="m-0"
                      />
                    )}
                  </span>
                )
              }}
            />
          )
        })}
        {/* Total column */}
        <Column
          field="weekSisuTime"
          header={
            screenWidth > BREAKPOINTS.MOBILE_BREAKPOINT ?
              "Horas trabajadas"
            : <p className="text-overflow-ellipsis overflow-hidden mb-0 text-overflow-ellipsis white-space-nowrap">
                Trabajadas
              </p>
          }
          hidden={isOnMobile && horizontalScrollCursor > 0}
          style={
            screenWidth > BREAKPOINTS.MOBILE_BREAKPOINT ?
              {
                minWidth: "103px",
              }
            : {
                minWidth: "0px",
                paddingRight: 0,
                paddingLeft: 0,
              }
          }
          body={(rowData) => {
            const teamMemberWeek = rowData?.week
            const nonWorkingDays = Object.keys(teamMemberWeek)?.reduce(
              (acc, date) => {
                const currentDay = teamMemberWeek[date]
                const teamMemberSchemaRules =
                  schemaRules[currentDay?.teamMemberHolidaySchemaId]
                const isHolidayAvailableForTeamMember = isHolidayAvailable({
                  idTypeOfHoliday: currentDay?.holidayType,
                  idHolidaySchemaType: currentDay?.holidaySchemaType,
                  teamMemberSchemaRules,
                  workAnniversaryDate: currentDay?.teamMemberWorkAnniversary,
                  currentDate: date,
                })
                if (
                  // currentDay.totalSisuTime === 0 ||
                  currentDay?.isVacationDay ||
                  (currentDay?.isHoliday && isHolidayAvailableForTeamMember)
                ) {
                  acc.push(date)
                }
                return acc
              },
              []
            )
            const timeWorkedThisWeek = 40 - nonWorkingDays.length * 8
            return (
              <span className="number-with-danger-badge flex align-items-center gap-1">
                {rowData?.weekSisuTime || "-"}
                {(
                  rowData?.weekSisuTime &&
                  isBeforeCurrentWeek(Object.keys(rowData?.week)[0])
                ) ?
                  <UnexpectedTimeBadge
                    value={rowData?.weekSisuTime}
                    expectedValue={timeWorkedThisWeek}
                    minThreshold={0.5}
                    maxThreshold={1}
                    referenceValue={timeWorkedThisWeek}
                    tooltips={{
                      isLess: "Se registraron menos horas de las esperadas",
                      isMore: "Se registraron más horas de las esperadas",
                    }}
                    tooltipProps={{
                      position: "bottom",
                    }}
                  />
                : null}
              </span>
            )
          }}
        />
        <Column
          hidden={isOnMobile && horizontalScrollCursor > 1}
          field="weekClientBilledTime"
          header={weekClientBilledTimeHeader()}
          style={
            screenWidth > BREAKPOINTS.MOBILE_BREAKPOINT ?
              {
                minWidth: "103px",
              }
            : {
                minWidth: "0px",
                paddingRight: 0,
                paddingLeft: horizontalScrollCursor === 0 ? 7 : 0,
              }
          }
        />
        <Column
          field="weekTimeDifference"
          header={weekTimeDifferenceHeader()}
          hidden={isOnMobile && horizontalScrollCursor < 1}
          style={
            screenWidth > BREAKPOINTS.MOBILE_BREAKPOINT ?
              {
                minWidth: "103px",
              }
            : {
                minWidth: "50px",
                paddingRight: 0,
                paddingLeft: horizontalScrollCursor === 2 ? 0 : 7,
              }
          }
          body={(rowData) => {
            let percentage = 0
            let total = 0
            let comments = []

            if (
              rowData?.taskTimeDifferenceComments !== undefined &&
              rowData?.taskTimeDifferenceComments.length !== 0
            ) {
              percentage = formatPercentageSuffix(
                rowData?.weekTimeDifference / rowData?.weekClientTime
              )
              total = rowData?.condensedTimeDifferenceData?.total

              const sortedData = rowData?.condensedTimeDifferenceData?.items
                .map((el) => ({ ...el, key: el.project + el.client }))
                .sort(
                  (a, b) =>
                    a.client.localeCompare(b.client) ||
                    a.key.localeCompare(b.key)
                )
              comments = sortedData.map(({ client, project, time }) => {
                return {
                  project: (
                    <div style={{ width: "85px", whiteSpace: "nowrap" }}>
                      <ClientProjectChip
                        clientCode={client}
                        projectCode={project}
                      />
                    </div>
                  ),
                  hours: time,
                }
              })
            }

            let onClick
            if (rowData?.taskTimeDifferenceComments.length !== 0) {
              const { teamMemberName, teamMemberLastName } = rowData
              const fullName = `${teamMemberName} ${teamMemberLastName}`
              const timeDifferences = rowData?.taskTimeDifferenceComments.map(
                (el) => {
                  const [client, project] = el.project.split(" - ")
                  return {
                    client: client.trim(),
                    project: project.trim(),
                    time: el.time.trim(),
                    comment: el.comment.trim(),
                    taskName: el.taskName,
                    taskId: el.taskId,
                    taskDate: el.taskDate,
                    teamMemberCode: el.teamMemberCode,
                    teamMemberId: el.teamMemberId,
                  }
                }
              )

              onClick = () => {
                setModalData({ fullName, timeDifferences, total })
                setIsTimeDiffDetailsModalOpen(true)
              }
            }

            return (
              <span className="flex justify-content-between align-items-center w-full">
                <span className="w-4 text-left">
                  {rowData?.weekClientTime > 0 ?
                    rowData?.weekTimeDifference
                  : "0"}
                </span>
                {rowData?.weekTimeDifference !== 0 && (
                  <span className="flex-grow-1 text-left">
                    <TimeDifferenceBadge
                      value={Math.abs(rowData?.weekTimeDifference)}
                      minTreshold={0}
                      maxTreshold={0}
                      badgeProps={{
                        onClick,
                        style: {
                          cursor: onClick ? "pointer" : "default",
                        },
                      }}
                      position="left"
                      referenceValue={Math.abs(rowData?.weekTimeDifference) - 1}
                      percentage={percentage}
                      tooltip={
                        isDateBeforeAIKALaunch ?
                          <table>
                            <tbody>
                              <tr>
                                <td className="text-center">Sin información</td>
                              </tr>
                            </tbody>
                          </table>
                        : <DifferenceReasonTable
                            header={["Proyecto", "Horas"]}
                            total={total}
                            body={comments}
                          />
                      }
                    />
                  </span>
                )}
              </span>
            )
          }}
        />
        <Column
          hidden={isOnMobile && horizontalScrollCursor < 2}
          field="ocupationRatio"
          header={
            <div className="flex align-items-center gap-2">
              <span>Ocupación</span>
              <IconWithTooltipAndCustomBody
                tooltipBody="Horas trabajadas para cliente con respecto a las horas trabajadas en total"
                position="top"
                iconClassName="pi pi-info-circle mx-1"
              />
            </div>
          }
          style={
            screenWidth > BREAKPOINTS.MOBILE_BREAKPOINT ?
              {
                minWidth: "103px",
              }
            : {
                minWidth: "71px",
                paddingRight: 0,
              }
          }
          body={(rowData) => percentageBody(rowData, "ocupationRatio")}
        />
      </DataTable>
    )
  }
)

export default WeekHoursDataTable
